import { useContext, useMemo, useState } from "react";

import FileInput, { IFileInputComponentProps } from "components/FileInput/FileInput";
import { DynamicFormContext } from "containers/DynamicForm/DynamicFormContext";
import { IDynamicFormItemProps } from "containers/DynamicForm/DynamicFormItems";
import { useTranslation } from "modules";

const DOCUMENT_UPLOAD_LIMIT = 20;
const DOCUMENT_SIZE_LIMIT = 20; // in MB

export function DirectUploadInputItem({ item }: IDynamicFormItemProps<"inputDirectUpload">) {
  const { form } = useContext(DynamicFormContext);
  const { t } = useTranslation();

  const { content, key } = item;
  const { attachable = [], disabled, multiple } = content;
  const { label, value } = form.getFormInput(key);

  // LOCAL STATE
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  // LOCAL DERIVED VARIABLES
  const acceptableFiles = useMemo(() => {
    return attachable?.join(",");
  }, [attachable]);

  // LOCAL METHODS
  const handleFileInputChange: IFileInputComponentProps["onChange"] = event => {
    form.onChange({
      target: { id: key, value: (event.target as HTMLInputElement).value, files: event.files },
    });
  };

  const onFileChangeError: IFileInputComponentProps["onFileChangeError"] = (errorName, file) => {
    if (errorName === "file-size-error") {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      const errorMessage = t(
        "direct_upload.input.file.size.error",
        "%<fileName>s size %<fileSize>sMB exceeds the limit of %<sizeLimit>sMB",
        {
          fileName: file.name,
          fileSize: sizeInMB,
          sizeLimit: DOCUMENT_SIZE_LIMIT,
        }
      );
      setErrorMessages(prevErrs => [...prevErrs, errorMessage]);
    } else if (errorName === "file-limit-error") {
      const errorMessage = t(
        "direct_upload.input.file.limit.error",
        "Maximum file upload limit exceeded (%<fileLimit>s)",
        {
          fileLimit: DOCUMENT_UPLOAD_LIMIT,
        }
      );
      setErrorMessages(prevErrs => [...prevErrs, errorMessage]);
    }
  };

  return (
    <FileInput
      accept={acceptableFiles}
      dataTestId={key}
      disabled={disabled}
      errorMessages={errorMessages}
      fileLimit={DOCUMENT_UPLOAD_LIMIT}
      id={key}
      label={label}
      multiple={multiple}
      onChange={handleFileInputChange}
      onClick={() => setErrorMessages([])}
      onFileChangeError={onFileChangeError}
      sizeLimit={DOCUMENT_SIZE_LIMIT}
      value={value as string | undefined}
    />
  );
}
